import { ThemeEvents } from '@theme/events';

class SellingPlanPicker extends HTMLElement {
  constructor() {
    super();
    this.hiddenInput = this.querySelector('input[name="selling_plan"]');
    this.cards = this.querySelectorAll('.selling-plan-picker__card');
    this.otpButton = this.querySelector('[data-otp-buy]');
    this.isSubmitting = false;
    this.loadingTimeout = null;
    this.onVariantUpdate = this.handleVariantUpdate.bind(this);
  }

  connectedCallback() {
    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);

    this.addEventListener('change', (e) => {
      const radio = e.target.closest('input[type="radio"]');
      if (!radio) return;

      this.updateSelectedCard(radio);

      if (this.hiddenInput) {
        this.hiddenInput.value = radio.value;
      }

      this.updateAddToCartPrice(radio);
      this.syncVariantSelection(radio);
    });

    if (this.otpButton) {
      this.otpButton.addEventListener('click', () => {
        // Uncheck all subscription radios so :has(input:checked) doesn't keep them visually selected
        this.querySelectorAll('input[type="radio"]').forEach(r => { r.checked = false; });

        this.cards.forEach(card => card.classList.remove('selling-plan-picker__card--selected'));
        this.otpButton.classList.add('selling-plan-picker__card--selected');

        if (this.hiddenInput) {
          this.hiddenInput.value = '';
        }

        this.syncOtpVariantSelection();

        const price = parseInt(this.otpButton.dataset.price, 10);
        const comparePrice = parseInt(this.otpButton.dataset.comparePrice, 10);
        this.updatePriceDisplay(price, comparePrice);
      });
    }

    const checkedRadio = this.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      this.updateSelectedCard(checkedRadio);
      this.updateAddToCartPrice(checkedRadio);
      this.syncVariantSelection(checkedRadio);
    }
  }

  disconnectedCallback() {
    const section = this.closest('.shopify-section, dialog');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  updateSelectedCard(radio) {
    this.cards.forEach((card) => card.classList.remove('selling-plan-picker__card--selected'));
    radio.closest('.selling-plan-picker__card')?.classList.add('selling-plan-picker__card--selected');
  }

  updateAddToCartPrice(radio) {
    const price = parseInt(radio.dataset.price, 10);
    const comparePrice = parseInt(radio.dataset.comparePrice, 10);
    this.updatePriceDisplay(price, comparePrice);
  }

  updatePriceDisplay(price, comparePrice) {
    const sectionId = this.dataset.sectionId;
    const section = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null;
    const scope = section || document;

    const currentEl = scope.querySelector('[ref="addToCartPriceCurrent"]');
    const compareEl = scope.querySelector('[ref="addToCartPriceCompare"]');
    if (!currentEl) return;

    currentEl.textContent = this.formatMoney(price);

    if (compareEl) {
      if (comparePrice > price) {
        compareEl.textContent = this.formatMoney(comparePrice);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }

  syncVariantSelection(radio) {
    const variantId = radio.dataset.variantId;
    if (!variantId) return;

    const sectionId = this.dataset.sectionId;
    const section = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null;
    const scope = section || document;

    const variantPickerRadio = scope.querySelector(`variant-picker input[data-variant-id="${variantId}"]`);
    if (variantPickerRadio instanceof HTMLInputElement) {
      if (!variantPickerRadio.checked) {
        variantPickerRadio.click();
      }
      return;
    }

    const variantPickerOption = scope.querySelector(`variant-picker option[data-variant-id="${variantId}"]`);
    if (variantPickerOption instanceof HTMLOptionElement) {
      const select = variantPickerOption.closest('select');
      if (select && select.value !== variantPickerOption.value) {
        select.value = variantPickerOption.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const variantInput = scope.querySelector('product-form-component input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
    }
  }

  handleVariantUpdate(event) {
    const variantId = event.detail.resource?.id?.toString();
    if (!variantId) return;

    const matchingRadio = this.querySelector(`input[type="radio"][data-variant-id="${variantId}"]`);
    if (matchingRadio instanceof HTMLInputElement) {
      matchingRadio.checked = true;
      this.updateSelectedCard(matchingRadio);

      if (this.hiddenInput) {
        this.hiddenInput.value = matchingRadio.value;
      }

      this.updateAddToCartPrice(matchingRadio);
    }
  }

  syncOtpVariantSelection() {
    const variantId = this.otpButton?.dataset.otpVariantId;
    if (!variantId) return;

    const sectionId = this.dataset.sectionId;
    const section = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null;
    const scope = section || document;

    const variantPickerRadio = scope.querySelector(`variant-picker input[data-variant-id="${variantId}"]`);
    if (variantPickerRadio instanceof HTMLInputElement) {
      if (!variantPickerRadio.checked) {
        variantPickerRadio.click();
      }
      return;
    }

    const variantPickerOption = scope.querySelector(`variant-picker option[data-variant-id="${variantId}"]`);
    if (variantPickerOption instanceof HTMLOptionElement) {
      const select = variantPickerOption.closest('select');
      if (select && select.value !== variantPickerOption.value) {
        select.value = variantPickerOption.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const variantInput = scope.querySelector('product-form-component input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
    }
  }

  submitProductForm() {
    const sectionId = this.dataset.sectionId;
    const formId = `BuyButtons-ProductForm-${sectionId}`;
    const form = document.getElementById(formId);
    const productForm = form?.closest('product-form-component');

    if (productForm) {
      const resetLoadingState = () => {
        this.setOtpLoadingState(false);
        productForm.removeEventListener('cart:update', resetLoadingState);
        productForm.removeEventListener('cart:error', resetLoadingState);
      };

      productForm.addEventListener('cart:update', resetLoadingState, { once: true });
      productForm.addEventListener('cart:error', resetLoadingState, { once: true });
    } else {
      this.setOtpLoadingState(false);
    }

    if (form) {
      form.requestSubmit();
    } else {
      this.setOtpLoadingState(false);
    }
  }

  setOtpLoadingState(isLoading) {
    this.isSubmitting = isLoading;

    if (this.loadingTimeout) {
      window.clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }

    if (!this.otpButton) return;

    this.otpButton.disabled = isLoading;
    this.otpButton.setAttribute('aria-busy', String(isLoading));
    this.otpButton.classList.toggle('adding', isLoading);

    if (isLoading) {
      this.loadingTimeout = window.setTimeout(() => {
        this.setOtpLoadingState(false);
      }, 8000);
    }
  }

  formatMoney(cents) {
    if (isNaN(cents)) return '';
    const amount = cents / 100;
    return Number.isInteger(amount)
      ? `$${amount}`
      : `$${amount.toFixed(2)}`;
  }
}

if (!customElements.get('selling-plan-picker')) {
  customElements.define('selling-plan-picker', SellingPlanPicker);
}
